/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';
import omit from 'lodash/omit';

import { Version } from '../../Common/types/CommonTypes';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet, fetchPost, fetchDelete, fetchPut } from 'FetchAPIs';
import { stripDevicesFromPath, stripSystemChunkFromLocationHierarchyPath } from 'hierarchyUtils';

const baseHierarchiesURL = '/api/hierarchies';

const messages = defineMessages({
  errorAddingKPI: 'Error adding the function. {message}',
  errorUpdatingKPI: 'Error updating function. {message}',
  errorDeletingKPI:
    'Error deleting the function. {message}.  Check the Dependencies tab for this data item to understand where this function is being used.',
  errorGettingKPIMetaData: 'Error loading the function. {message}',
  errorGettingAvailableKPIs: 'Error loading the function catalog. {message}',
  errorFetchingKPIs: 'Error fetching functions. {message}',
  // errorDeactivatingFunction: 'Error deactivating function. {message}',
  errorLoadingDataItem: 'Error loading the data item. {message}',
});

/** get a list of KPIs by category */
export function getCatalogFunctions(category, version = [Version.V1]) {
  const params = {
    version: version.toString(),
  };
  if (category) {
    params.category = category;
  }

  return fetchGet(`${baseHierarchiesURL}/catalogFunctions`, null, params)
    .then(response =>
      Array.isArray(response)
        ? response.map(kpi => {
            /* eslint-disable no-param-reassign */
            kpi.input = kpi.input.map(input =>
              typeof input.jsonSchema === 'string'
                ? { ...input, jsonSchema: JSON.parse(input.jsonSchema) } // TODO: workaround to parse the jsonSchema if passed as string
                : input
            );
            /* eslint-enable no-param-reassign */
            return kpi;
          })
        : response
    )
    .catch(error => {
      throw new I18NError(messages.errorGettingAvailableKPIs, {
        message: formatErrorResponse(error),
      });
    });
}

/**
 * Adds the new function
 */

export function addKPI(hierarchyPath, kpiFunction, userEmailId) {
  // Make sure functionName is copied to catalogFunction, so that there is no need to replace every occurrence of functioName with catalogFunctionName in the code
  // TODO: Check with backend team if it is possible to rollback to functionName instead of catalogFunctionName. Other places in the code base might break after this name change
  // Another backend change: granularity is now granularityName
  return fetchPost(
    `${baseHierarchiesURL}${stripSystemChunkFromLocationHierarchyPath(hierarchyPath)}/kpiFunctions`,
    {
      ...omit(kpiFunction, ['inputMeta', 'granularity', 'functionName', 'version']),
      catalogFunctionName: kpiFunction.functionName || kpiFunction.catalogFunctionName,
      granularityName: kpiFunction.granularity || kpiFunction.granularityName,
      catalogFunctionVersion: kpiFunction.version,
      enabled: true,
    },
    { userEmailId }
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorAddingKPI, {
        message: formatErrorResponse(error),
      });
    });
}

/**
 *
 * Updates existing KPI
 */

export function updateAComplexKPI(hierarchyPath, functionId, kpiFunction, userEmailId) {
  // Use the kpi id to update the original KPI
  // Make sure functionName is copied to catalogFunction, so that there is no need to replace every occurrence of functioName with catalogFunctionName in the code
  // TODO: Check with backend team if it is possible to rollback to functionName instead of catalogFunctionName. Other places in the code base might break after this name change
  // Another backend change: granularity is now granularityName
  return fetchPut(
    `${baseHierarchiesURL}${stripSystemChunkFromLocationHierarchyPath(
      hierarchyPath
    )}/kpiFunctions/${functionId}`,
    {
      ...omit(kpiFunction, ['granularity', 'functionName']),
      catalogFunctionName: kpiFunction.functionName || kpiFunction.catalogFunctionName,
      granularityName: kpiFunction.granularity || kpiFunction.granularityName,
      enabled: true,
    },
    { userEmailId }
  ).catch(error => {
    throw new I18NError(messages.errorUpdatingKPI, {
      message: formatErrorResponse(error),
    });
  });
}

/**
 *  Returns an updated list of KPIs
 */
export function removeKPI(hierarchyPath, kpi, userEmailId) {
  return fetchDelete(
    `${baseHierarchiesURL}${stripSystemChunkFromLocationHierarchyPath(
      hierarchyPath
    )}/kpiFunctions/${kpi.name}`,
    null,
    {
      userEmailId,
    }
  ).catch(error => {
    throw new I18NError(messages.errorDeletingKPI, {
      message: formatErrorResponse(error),
    });
  });
}

export function getKPIs(hierarchyPath) {
  return fetchGet(
    `${baseHierarchiesURL}${stripDevicesFromPath(
      stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
    )}/kpiFunctions`,
    null,
    {
      excludeKPIFunctions: true,
    }
  ).catch(error => {
    throw new I18NError(messages.errorFetchingKPIs, {
      message: formatErrorResponse(error),
    });
  });
}

/**
 *
 * @param {string} hierarchyPath the path to the resource (exemple: /deviceType/{deviceTypeId})
 * @param {string} dataItem  the name of the dataItem
 * @returns {object} the kpiFunctionDto with details about the kpi
 */
export function getKPIDetails(hierarchyPath, dataItem) {
  return fetchGet(
    `${baseHierarchiesURL}${stripDevicesFromPath(
      stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
    )}/dataItems/${dataItem}`
  ).catch(error => {
    throw new I18NError(messages.errorLoadingDataItem, error);
  });
}
